// Pronostics - Prédit le score du prochain match
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ArrowLeft, Loader2, Trophy, Lock, Calendar, Clock, ChevronUp, ChevronDown, Check } from 'lucide-react';
import { GAMES_CONFIG } from '../services/fanZoneConfig';
import { saveGameScore, getUserProfile, getOrCreateUserProfile } from '../services/fanZoneService';
import { fetchMatchs } from '../../../api/fff';
import { db } from '../../../firebase';
import { collection, query, where, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import logger from '../../../utils/logger';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

// Scrollbar styles
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar { width: 6px; }
  .fanzone-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
`;

const ScoreSelector = ({ value, onChange, label, disabled }) => {
  return (
    <div className="flex flex-col items-center">
      <span className="text-gray-400 text-sm mb-2">{label}</span>
      <div className="flex flex-col items-center gap-1">
        <button
          onClick={() => !disabled && onChange(Math.min(9, value + 1))}
          disabled={disabled}
          className="w-12 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors disabled:opacity-50"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
        
        <div 
          className="w-16 h-20 rounded-xl flex items-center justify-center text-4xl font-bold text-white"
          style={{
            background: 'linear-gradient(135deg, rgba(255,228,0,0.1) 0%, rgba(189,255,0,0.05) 100%)',
            border: '1px solid rgba(255,228,0,0.3)'
          }}
        >
          {value}
        </div>
        
        <button
          onClick={() => !disabled && onChange(Math.max(0, value - 1))}
          disabled={disabled}
          className="w-12 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors disabled:opacity-50"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

const MatchCard = ({ match, userPronostic, onPronosticClick, isLocked }) => {
  const matchDate = dayjs(match.date);
  const isToday = matchDate.isSame(dayjs(), 'day');
  const isTomorrow = matchDate.isSame(dayjs().add(1, 'day'), 'day');
  
  const dateLabel = isToday ? "Aujourd'hui" : isTomorrow ? "Demain" : matchDate.format('DD/MM');
  // L'API FFF retourne time dans un format "18H00"
  let timeLabel = matchDate.format('HH:mm');
  if (match.time && typeof match.time === 'string') {
    const timeMatch = match.time.match(/(\d{1,2})H(\d{2})/);
    if (timeMatch) {
      timeLabel = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        p-4 rounded-xl border transition-all duration-200
        ${isLocked 
          ? 'bg-white/[0.02] border-white/5 opacity-60' 
          : 'bg-white/[0.05] border-white/10 hover:border-[#FFE400]/30 cursor-pointer'
        }
      `}
      onClick={() => !isLocked && onPronosticClick(match)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-gray-300">{dateLabel}</span>
          <Clock className="w-4 h-4 text-gray-400 ml-2" />
          <span className="text-gray-300">{timeLabel}</span>
        </div>
        
        {isLocked ? (
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/10 text-red-400 text-xs">
            <Lock className="w-3 h-3" />
            <span>Verrouillé</span>
          </div>
        ) : userPronostic ? (
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs">
            <Check className="w-3 h-3" />
            <span>Pronostiqué</span>
          </div>
        ) : null}
      </div>
      
      <div className="flex items-center justify-between">
        {/* Home team */}
        <div className="flex-1 text-center">
          <div className="text-white font-bold">{match.homeTeam || 'Équipe dom.'}</div>
        </div>
        
        {/* Score / VS */}
        <div className="px-4">
          {userPronostic ? (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-[#FFE400]">{userPronostic.homeScore}</span>
              <span className="text-gray-500">-</span>
              <span className="text-2xl font-bold text-[#FFE400]">{userPronostic.awayScore}</span>
            </div>
          ) : (
            <span className="text-gray-500 font-medium">VS</span>
          )}
        </div>
        
        {/* Away team */}
        <div className="flex-1 text-center">
          <div className="text-white font-bold">{match.awayTeam || 'Équipe ext.'}</div>
        </div>
      </div>
      
      <div className="mt-3 text-center text-xs text-gray-500">
        {match.competition || 'National 2'}
      </div>
    </motion.div>
  );
};

const PronosticsGame = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const config = GAMES_CONFIG.pronostics;
  
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [userPronostics, setUserPronostics] = useState({});
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Injecter styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    return () => {
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  // Helper pour extraire le nom d'équipe (peut être string ou objet)
  const getTeamName = (team, fallback = 'Équipe') => {
    if (!team) return fallback;
    if (typeof team === 'string') return team;
    if (typeof team === 'object') {
      return team.short_name || team.name || team.cl_nom || fallback;
    }
    return fallback;
  };

  // Charger les matchs et pronostics
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        
        // Charger les prochains matchs
        let formattedMatches = [];
        try {
          const { upcoming } = await fetchMatchs();
          
          // Formater les matchs - L'API FFF utilise home/away pas homeTeam/awayTeam
          formattedMatches = (upcoming || []).slice(0, 5).map(match => ({
            id: match.id || match.ma_no || `match_${Date.now()}_${Math.random()}`,
            date: match.date || match.ma_dat || new Date().toISOString(),
            time: match.time || null,
            homeTeam: match.home?.short_name_federation || match.home?.short_name || getTeamName(match.home, 'EFAFC'),
            awayTeam: match.away?.short_name_federation || match.away?.short_name || getTeamName(match.away, 'Adversaire'),
            competition: match.competition?.name || 'National 2'
          }));
        } catch (e) {
          logger.warn('Erreur chargement matchs API, utilisation démo');
          // Matchs de démonstration
          formattedMatches = [
            {
              id: 'demo_1',
              date: dayjs().add(2, 'day').set('hour', 15).toISOString(),
              homeTeam: 'EFAFC',
              awayTeam: 'US Avion',
              competition: 'National 2'
            },
            {
              id: 'demo_2',
              date: dayjs().add(9, 'day').set('hour', 18).toISOString(),
              homeTeam: 'Wasquehal',
              awayTeam: 'EFAFC',
              competition: 'National 2'
            }
          ];
        }
        
        setMatches(formattedMatches);
        
        // Charger les pronostics de l'utilisateur (ignorer erreurs permission)
        try {
          const pronosticsRef = collection(db, 'fanzone_pronostics');
          const q = query(pronosticsRef, where('userId', '==', currentUser.uid));
          const snapshot = await getDocs(q);
          
          const pronostics = {};
          snapshot.forEach(docSnap => {
            const data = docSnap.data();
            pronostics[data.matchId] = {
              homeScore: data.homeScore,
              awayScore: data.awayScore,
              docId: docSnap.id
            };
          });
          
          setUserPronostics(pronostics);
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour pronostics');
        }
        
      } catch (error) {
        logger.error('Erreur chargement pronostics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, navigate]);

  const isMatchLocked = (match) => {
    const matchDate = dayjs(match.date);
    const lockTime = matchDate.subtract(1, 'hour');
    return dayjs().isAfter(lockTime);
  };

  const handlePronosticClick = (match) => {
    if (isMatchLocked(match)) {
      toast.error('Les pronostics sont verrouillés 1h avant le match');
      return;
    }
    
    const existing = userPronostics[match.id];
    if (existing) {
      setHomeScore(existing.homeScore);
      setAwayScore(existing.awayScore);
    } else {
      setHomeScore(0);
      setAwayScore(0);
    }
    
    setSelectedMatch(match);
  };

  const handleSubmitPronostic = async () => {
    if (!selectedMatch || submitting) return;
    
    try {
      setSubmitting(true);
      
      // Sauvegarder le pronostic (ignorer erreurs permission)
      try {
        await addDoc(collection(db, 'fanzone_pronostics'), {
          userId: currentUser.uid,
          matchId: selectedMatch.id,
          homeScore,
          awayScore,
          homeTeam: selectedMatch.homeTeam,
          awayTeam: selectedMatch.awayTeam,
          matchDate: selectedMatch.date,
          createdAt: new Date()
        });
      } catch (e) {
        logger.warn('Impossible de sauvegarder en base, stockage local uniquement');
      }
      
      // Mettre à jour l'état local dans tous les cas
      setUserPronostics(prev => ({
        ...prev,
        [selectedMatch.id]: { homeScore, awayScore }
      }));
      
      toast.success('Pronostic enregistré !');
      setSelectedMatch(null);
      
    } catch (error) {
      logger.error('Erreur sauvegarde pronostic:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <Loader2 className="w-8 h-8 text-[#FFE400] animate-spin" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
    >
      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="text-4xl mb-2">⚽</div>
          <h1 className="text-2xl font-bold text-white">Pronostics</h1>
          <p className="text-gray-400 text-sm mt-1">
            Prédit les scores des prochains matchs
          </p>
        </motion.div>

        {/* Points info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10"
        >
          <div className="text-center mb-3">
            <Trophy className="w-6 h-6 text-[#FFE400] mx-auto mb-2" />
            <span className="text-white font-medium">Barème des points</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <div className="text-[#BDFF00] font-bold">{config.points.scoreExact}</div>
              <div className="text-gray-400">Score exact</div>
            </div>
            <div>
              <div className="text-[#FFE400] font-bold">{config.points.bonEcart}</div>
              <div className="text-gray-400">Bon écart</div>
            </div>
            <div>
              <div className="text-gray-300 font-bold">{config.points.bonResultat}</div>
              <div className="text-gray-400">Bon résultat</div>
            </div>
          </div>
        </motion.div>

        {/* Matches list */}
        <div className="space-y-3">
          {matches.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucun match à venir pour le moment</p>
            </div>
          ) : (
            matches.map(match => (
              <MatchCard
                key={match.id}
                match={match}
                userPronostic={userPronostics[match.id]}
                onPronosticClick={handlePronosticClick}
                isLocked={isMatchLocked(match)}
              />
            ))
          )}
        </div>

        {/* Pronostic Modal */}
        <AnimatePresence>
          {selectedMatch && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
              onClick={() => setSelectedMatch(null)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0d1526] border border-white/10 rounded-2xl p-6 max-w-sm w-full"
              >
                <h3 className="text-xl font-bold text-white text-center mb-2">
                  Ton pronostic
                </h3>
                <p className="text-gray-400 text-center text-sm mb-6">
                  {dayjs(selectedMatch.date).format('DD/MM/YYYY à HH:mm')}
                </p>
                
                {/* Teams & Score */}
                <div className="flex items-center justify-center gap-4 mb-8">
                  <div className="flex-1 text-center">
                    <div className="text-white font-bold mb-2">{selectedMatch.homeTeam}</div>
                    <ScoreSelector
                      value={homeScore}
                      onChange={setHomeScore}
                      label="Dom."
                      disabled={submitting}
                    />
                  </div>
                  
                  <div className="text-gray-500 text-xl font-bold">-</div>
                  
                  <div className="flex-1 text-center">
                    <div className="text-white font-bold mb-2">{selectedMatch.awayTeam}</div>
                    <ScoreSelector
                      value={awayScore}
                      onChange={setAwayScore}
                      label="Ext."
                      disabled={submitting}
                    />
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedMatch(null)}
                    className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white transition-colors"
                    disabled={submitting}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmitPronostic}
                    disabled={submitting}
                    className="flex-1 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                    ) : (
                      'Valider'
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PronosticsGame;
